import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api } from "./client";
import type {
  NvrClip,
  NvrClipList,
  NvrClipRequest,
  PlaybackResponse,
  RecordingConfig,
  RecordingConfigList,
  RecordingConfigUpsert,
  RecordingList,
} from "@/types";

const recKey = ["recordings"] as const;
const cfgKey = ["recording_config"] as const;
const nvrKey = ["nvr_clips"] as const;

// ---------------------------------------------------------------------------
// Recording config
// ---------------------------------------------------------------------------
export function useRecordingConfigs() {
  return useQuery({
    queryKey: [...cfgKey, "list"],
    queryFn: async () => {
      const { data } = await api.get<RecordingConfigList>(
        "/api/v1/recording/config",
      );
      return data;
    },
  });
}

export function useRecordingConfig(cameraId: string | undefined) {
  return useQuery({
    queryKey: [...cfgKey, "detail", cameraId],
    queryFn: async () => {
      const { data } = await api.get<RecordingConfig | null>(
        `/api/v1/recording/config/${cameraId}`,
      );
      return data;
    },
    enabled: Boolean(cameraId),
  });
}

export function useUpsertRecordingConfig(cameraId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: RecordingConfigUpsert) => {
      const { data } = await api.post<RecordingConfig>(
        `/api/v1/recording/config/${cameraId}`,
        payload,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: cfgKey });
    },
  });
}

// ---------------------------------------------------------------------------
// Recordings + playback
// ---------------------------------------------------------------------------
export function useRecordings(opts?: {
  cameraId?: string;
  date?: string; // YYYY-MM-DD
  limit?: number;
}) {
  return useQuery({
    queryKey: [...recKey, "list", opts ?? {}],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (opts?.cameraId) params.set("camera_id", opts.cameraId);
      if (opts?.date) params.set("date", opts.date);
      if (opts?.limit) params.set("limit", String(opts.limit));
      const qs = params.toString();
      const { data } = await api.get<RecordingList>(
        `/api/v1/recordings${qs ? `?${qs}` : ""}`,
      );
      return data;
    },
  });
}

export function usePlayRecording(recordingId: string | undefined) {
  return useQuery({
    queryKey: [...recKey, "play", recordingId],
    queryFn: async () => {
      const { data } = await api.get<PlaybackResponse>(
        `/api/v1/recordings/${recordingId}/play`,
      );
      return data;
    },
    enabled: Boolean(recordingId),
    // Playlists embed presigned URLs that expire in 1hr; refresh just
    // before that window closes if the user is still on the page.
    staleTime: 50 * 60 * 1000,
  });
}

// ---------------------------------------------------------------------------
// NVR clips
// ---------------------------------------------------------------------------
export function useNvrClips(opts?: { cameraId?: string }) {
  return useQuery({
    queryKey: [...nvrKey, "list", opts ?? {}],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (opts?.cameraId) params.set("camera_id", opts.cameraId);
      const qs = params.toString();
      const { data } = await api.get<NvrClipList>(
        `/api/v1/nvr/clips${qs ? `?${qs}` : ""}`,
      );
      return data;
    },
    // Poll every 10s — the queue contains rows the recorder agent
    // is actively processing, so the UI should reflect state churn.
    refetchInterval: (q) => {
      const items = (q.state.data as NvrClipList | undefined)?.items ?? [];
      const inflight = items.some(
        (it) => it.status === "pending" || it.status === "downloading",
      );
      return inflight ? 10_000 : false;
    },
  });
}

export function useNvrClip(id: string | undefined) {
  return useQuery({
    queryKey: [...nvrKey, "detail", id],
    queryFn: async () => {
      const { data } = await api.get<NvrClip>(`/api/v1/nvr/clips/${id}`);
      return data;
    },
    enabled: Boolean(id),
  });
}

export function useRequestNvrClip() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: NvrClipRequest) => {
      const { data } = await api.post<NvrClip>(
        "/api/v1/nvr/clips/request",
        payload,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: nvrKey });
    },
  });
}
